import React, {
  useEffect, useMemo, useState,
} from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  IconButton,
  Stack, Tooltip, Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';

import Autocomplete from '../../../../components/Autocomplete';
import Chip from '../../../../components/Chip';
import ContentCard from '../../../../components/ContentCard';
import { DateTimePicker } from '../../../../components/DatePicker';
import Modal from '../../../../components/Modal';
import SearchField from '../../../../components/SearchField';
import Tabs from '../../../../components/Tabs';
import { useAddLessonMutation, useAddLessonsBatchMutation } from '../../../../services/classes';
import { Class, Lesson } from '../../../../services/classes/types';
import { useGetLessonTemplatesQuery } from '../../../../services/lessons';
import { useGetStreamQuery } from '../../../../services/streams';
import { Metadata, TypeOption } from '../../../../services/types';
import addDays from '../../../../utils/common/addDays';
import useError from '../../../../utils/react/hooks/useError';
import useSuccess from '../../../../utils/react/hooks/useSuccess';
import ClassLessonCard from '../../ClassLessonCard';

enum ScheduleType {
  Single = 'single',
  Bulk = 'bulk',
}

const scheduleTypes = [
  {
    label: 'Single Lesson',
    key: ScheduleType.Single,
  },
  {
    label: 'Bulk Schedule',
    key: ScheduleType.Bulk,
  },
];

const isClassDay = (classObj: Class, date: Dayjs) => {
  const day = date.day();
  const classDay = classObj.day;
  return classDay === day;
};

const isClassTime = (classObj: Class, date: Dayjs) => {
  const classTime = parseInt(classObj.time ?? '0', 10);
  const time = date.hour() * 100 + date.minute();
  return classTime === time;
};

// This only works where the lessons are ordered by startDatetime
const calculateNextLessonDate = (classObj: Class, lessons?: Lesson[]) => {
  const initialDate = (!lessons || lessons.length === 0) ?
    dayjs() : dayjs(lessons?.at(-1)?.startDatetime);

  // Dayjs day starts at 0 for sunday, but classObj day starts at 1 for sunday
  const classDay = classObj.day;

  // Find the first day in the following week (starting monday) that matches the classObj day
  const daysToAdd = classDay - initialDate.day() + 7;

  const classTime = parseInt(classObj.time ?? '0', 10);

  // Return date with the same time as the classObj time which is in the form of HHmm
  return dayjs(addDays(initialDate, daysToAdd).valueOf())
    .set('hour', Math.floor(classTime / 100))
    .set('minute', classTime % 100)
    .set('second', 0)
    .set('millisecond', 0);
};

interface LessonsCardProps {
  classObj: Class;
  lessons?: Lesson[];
  selectedLesson?: Lesson;
  lessonMetadata?: Metadata;
  onSelectLesson: (lesson: Lesson) => void;
  refetchLessons?: () => void;
}

const LessonsCard = ({ classObj,
  lessons,
  selectedLesson,
  lessonMetadata,
  onSelectLesson,
  refetchLessons = () => {} }: LessonsCardProps) => {
  const [addLesson, {
    isLoading: isAddLessonLoading,
    isError: isAddLessonError,
    error: addLessonError,
    isSuccess: isAddLessonSuccess,
  }] = useAddLessonMutation();
  const [bulkAddLessons, {
    isLoading: isBulkAddLessonsLoading,
    isError: isBulkAddLessonsError,
    error: bulkAddLessonsError,
    isSuccess: isBulkAddLessonsSuccess,
  }] = useAddLessonsBatchMutation();

  const {
    data: courseLessonTemplates,
    isLoading: isCourseLessonTemplatesLoading,
  } = useGetLessonTemplatesQuery(
    { course: classObj.course }, { skip: !classObj.course },
  );
  const { data: stream } = useGetStreamQuery(
    { course: classObj.course, streamId: classObj.stream });

  const [scheduleType, setScheduleType] = useState(ScheduleType.Bulk);

  const [showMarkedLessons, setShowMarkedLessons] = useState(false);
  const [isAddLessonOpen, setIsAddLessonOpen] = useState(false);
  const [selectedLessonId, setselectedLessonId] = useState();
  const [startDatetime, setStartDatetime] = useState(calculateNextLessonDate(classObj, lessons));
  const [billingType, setBillingType] = useState<TypeOption>({
    label: 'Extra',
    value: 'EXTRA',
  });

  const [searchStr, setSearchStr] = useState('');

  const lessonOptions: {
    value: string,
    label: string
  }[] = useMemo(() => {
    const lessonTemplates = courseLessonTemplates?.data;
    if (lessonTemplates) {
      if (scheduleType === ScheduleType.Single) {
        return lessonTemplates.map((template) => ({
          value: template.lessonId,
          label: template.combinedLabel,
        })) ?? [];
      } if (scheduleType === ScheduleType.Bulk) {
        return stream?.data.orderedLessons.map((lessonId) => ({
          value: lessonId,
          label: lessonTemplates?.find((x) => x.lessonId === lessonId)?.combinedLabel ?? lessonId,
        })) ?? [];
      }
    }

    return [];
  }, [scheduleType, courseLessonTemplates, stream]);

  const onClose = () => {
    setIsAddLessonOpen(false);
    setselectedLessonId(undefined);
    setSearchStr('');
    setScheduleType(ScheduleType.Bulk);
  };

  useEffect(() => {
    setStartDatetime(calculateNextLessonDate(classObj, lessons));
  }, [classObj, lessons]);

  useEffect(() => {
    if (isAddLessonSuccess || isBulkAddLessonsSuccess) {
      onClose();
      refetchLessons();
    }
  }, [isAddLessonSuccess, isBulkAddLessonsSuccess]);

  const filteredLessons = useMemo(() => lessons?.filter(
    (lesson) => showMarkedLessons || !lesson.rollMarked,
  ), [lessons, showMarkedLessons]);

  const handleScheduleLesson = () => {
    if (scheduleType === ScheduleType.Single) {
      addLesson({
        subject: classObj.subject,
        classId: classObj.classId,
        course: classObj.course,
        lessonId: selectedLessonId ?? '',
        startDatetime: startDatetime.valueOf(),
        attributes: {
          billingType: billingType.value,
        },
      });
    } else {
      bulkAddLessons({
        subject: classObj.subject,
        classId: classObj.classId,
        lessonId: selectedLessonId ?? '',
        startDatetime: startDatetime.valueOf(),
      });
    }
  };

  useError(isAddLessonError, addLessonError);
  useSuccess(isAddLessonSuccess, 'Lesson added');

  useError(isBulkAddLessonsError, bulkAddLessonsError);
  useSuccess(isBulkAddLessonsSuccess, 'Lessons added');

  return (
    <ContentCard
      variant="outlined"
      title="Lessons"
      buttons={[
        {
          label: 'Add Lesson',
          onClick: () => setIsAddLessonOpen(true),
          disabled: !classObj.active,
        },
      ]}
    >
      <Chip
        label="Upcoming Only"
        onClick={() => setShowMarkedLessons((prev) => !prev)}
        variant={showMarkedLessons ? 'outlined' : 'filled'}
        color="gray"
        cursorType="pointer"
      />
      <Stack direction="column" spacing={1} marginTop={2}>
        {
          filteredLessons?.map((lesson) => (
            <ClassLessonCard
              key={lesson.id}
              lesson={lesson}
              selected={lesson.id === selectedLesson?.id}
              onSelect={() => onSelectLesson(lesson)}
              // If this is the first unmarked lesson, show new count
              showNewCount={lesson.id === filteredLessons[0].id && !lesson.rollMarked}
            />
          ))
        }
      </Stack>
      <Modal
        open={isAddLessonOpen}
        handleClose={onClose}
        title="Add Lesson"
        buttons={
          [
            {
              label: 'Add',
              onClick: () => handleScheduleLesson(),
              color: 'darkPrimary',
              loading: isAddLessonLoading || isBulkAddLessonsLoading,
            },
          ]
        }
      >
        <Stack direction="column" spacing={2}>
          <Tabs
            selectedKey={scheduleType}
            onChange={(tab) => setScheduleType(tab as ScheduleType)}
            options={scheduleTypes}
            dense
          />
          <Typography>
            {scheduleType === ScheduleType.Bulk ? 'Select first lesson' : 'Select lesson'}
          </Typography>
          <SearchField
            type="dropdown"
            loading={isCourseLessonTemplatesLoading}
            placeholder="Search lessons..."
            options={lessonOptions.filter((l) =>
              l.label.toLowerCase().includes(searchStr.toLowerCase()),
            ) || []}
            getOptionKey={(l) => l.value}
            getOptionLabel={(l) => l.label}
            onSearch={(search) => setSearchStr(search)}
            onSelect={(l) => setselectedLessonId(l.value)}
            clearOnSelect
          />
          {selectedLessonId && (
            <Stack direction="column" spacing={2}>
              <Typography>
                {selectedLessonId} commences on
              </Typography>
              <DateTimePicker
                value={startDatetime}
                onChange={(newDate) => {
                  if (newDate) {
                    setStartDatetime(newDate);
                  }
                }}
                shouldDisableDate={(date) => scheduleType === 'bulk' && !isClassDay(classObj, date)}
                shouldDisableTime={(date) => scheduleType === 'bulk' && !isClassTime(classObj, date)}
              />
              {scheduleType === ScheduleType.Single && (
                <Stack direction="column" spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography>
                      Choose billing type
                    </Typography>
                    <Tooltip title={`This determines whether students should be billed extra for this lesson.
                    This should almost always be set to "Extra" unless you deleted a lesson and are re-adding it.`}
                    >
                      <IconButton size="small">
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Autocomplete
                    options={lessonMetadata?.billingType.typeOptions ?? []}
                    value={billingType}
                    getOptionLabel={(option) => option.label}
                    onChange={(event: any, newValue: TypeOption) => {
                      setBillingType(newValue);
                    }}
                  />
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </Modal>
    </ContentCard>
  );
};
export default LessonsCard;
